/* eslint-disable max-depth */
/* eslint-disable guard-for-in */
import CrosshairFlagY from '../flags/CrosshairFlagY.js';
import CrosshairFlagX from '../flags/CrosshairFlagX.js';

/**
 * Creates object with the OHLCV or custom data to be emitted for the current crosshair position
 * @param {Panel} activePanel - Panel that is active (mouse is currently hovering over)
 * @param {Date} date - Date for relevant data point, used to get appropriate chart data
 * @param {Number} newX - Chart data point X coordinate closest to mouse X coordinate
 * @param {Number} newY - Distance from top of active panel to Mouse Y coordinate
 * @param {Number} dataY - Vertical location (in pixels) of the current chart data point under the crosshair
 */
export function buildCrosshairData(activePanel, date, newX, newY, dataY) {
	const { core } = activePanel;
	const dataOut = {
		date,
		coords: {
			x: newX,
			y: newY,
			dataY
		},
		activePanel
	};
	const dataOHLC = core.getDataByType(core.params.symbol, 'price');
	const dataVolume = core.getDataByType(core.params.symbol, 'volume');
	let dataIndex = core._getIndexByDate(core.dataPrimary, date);
	let dp;

	// OHLC data
	if (dataOHLC.length) {
		dp = dataOHLC[0][dataIndex];
		dataOut.open = dp.open;
		dataOut.high = dp.high;
		dataOut.low = dp.low;
		dataOut.close = dp.close;
	}

	// Add volume data
	if (dataVolume.length && dataVolume[0][dataIndex]) {
		dataOut.volume = dataVolume[0][dataIndex].volume;
	}

	// Get indicator data from all panels.  This is used when the simple data above isn't sufficient for more complex crosshair usage, like showing lower panel information or comparison data.
	dataOut.panels = [];
	core.panels.forEach(panel => {
		const panelData = {
			uid: panel.params.uid
		};
		const indicators = [];
		for (const ind of panel.indicators) {
			// Don't add info for noClip indicators
			if (ind.params.noClip) continue;

			// Get indicator data for this panel
			const { id, uid } = ind.params;
			const indParams = { id, uid };

			// Get standard data (OHLC for primary symbol)
			if (core.data[core.params.symbol]) {
				// Get standard indicator data for this panel
				const standardSymbol = ind.params.symbol || core.params.symbol;
				const symbolData = core.data[standardSymbol];

				// Does data exist for this indicator ID and UID?
				if (
					symbolData[id] &&
					symbolData[id][uid]
				) {
					dataIndex = ind.params.markerType === 'stepped' ?
						core._getPrevIndexByDate(symbolData[id][uid], date) :
						core._getIndexByDate(symbolData[id][uid], date);
					// Does data exist for this index?
					if (symbolData[id][uid][dataIndex]) {
						const data = symbolData[id][uid][dataIndex];
						indParams.symbol = standardSymbol;

						// Get x/y coords for this indicator
						const y = {};
						for (const key in data) {
							const value = data[key];

							// Skip date/text values
							if (
								!/^date/.test(key) &&
								!isNaN(Number(value))
							) {
								y[key] = Number(ind.yScale(value));
							}
						}

						const newPanelIndicator = {
							coords: {
								x: newX,
								y
							},
							data,
							indicator: ind,
							panel: ind.panel,
							params: indParams
						};

						// Convert normalized values to actuals (CH-4821)
						if (
							ind.panel.isNormalized &&
							core.state.normalizeValues?.indicators &&
							core.state.normalizeValues.indicators[indParams.uid]
						) {
							const normalizedValues = core.state.normalizeValues.indicators[indParams.uid];
							newPanelIndicator.data.actual = {
								close: core.getActualFromNormalized(data.close, normalizedValues.close),
								high: core.getActualFromNormalized(data.high, normalizedValues.high),
								low: core.getActualFromNormalized(data.low, normalizedValues.low),
								open: core.getActualFromNormalized(data.open, normalizedValues.open)
							};
						}

						indicators.push(newPanelIndicator);
					}
				}
			} else if (core.params.customData) { // Get custom data
				const { data: customData } = core;
				const { datasetId } = ind.params;

				if (customData[datasetId]) {
					// CH-2060 Can't go by primary index, have to use date since custom indices could differ
					const customIndex = core.crosshair._getParam('returnClosestCustomData') // This param may need to be placed in a more global location
						? core._getIndexByDate(customData[datasetId], date)
						: core._getIndexByDate(customData[datasetId], date, true);
					const y = {};
					let data = {};
					indParams.datasetId = datasetId;

					if (customIndex > -1) {
						// Convert data values to coordinate values
						data = { ...customData[datasetId][customIndex] };
						for (const key in data) {
							const value = data[key];

							// Skip date/text values
							if (
								!/^date/.test(key) &&
								!isNaN(Number(value))
							) {
								y[key] = Number(ind.yScale(value));
							}
						}
					} else {
						data = { date, value: null };
						y.value = null;
					}

					indicators.push({
						coords: {
							x: newX,
							y
						},
						data,
						indicator: ind,
						panel: ind.panel,
						params: indParams
					});
				}
			}
		}

		// Add indicator info to panel object
		panelData.indicators = [...indicators];

		dataOut.panels.push(panelData);
	});

	// currency
	if (core.state.currency) dataOut.currency = core.state.currency;

	// timezone label
	if (core.state.timezoneLabel) dataOut.timezoneLabel = core.state.timezoneLabel;

	// utc offset
	const { symbol } = core.params;
	if (symbol) {
		dataOut.utcOffset = core.xref[symbol] ? core.xref[symbol].utcOffset : null;
		dataOut.issueType = core.xref[symbol] ? core.xref[symbol].issueType : null;
	}

	return dataOut;
}

/**
 * Adds crosshair flags to given panel
 * @param {Panel} panel - Panel to add flags to
 */
export function addCrosshairFlags(panel) {
	// Create the flag and circle on the upper panel only
	if (panel.isUpper) {
		// Create flag if it doesn't exist
		if (!panel.flags['crosshair.yAxis']) {
			panel.flags['crosshair.yAxis'] = new CrosshairFlagY({
				panel,
				id: 'crosshair.yAxis'
			});
		}
	}

	// Create the optional XAxis flag
	if (
		!panel.flags['crosshair.xAxis'] &&
		panel.xAxis.hasAxis() &&
		panel.core.params.showXAxisFlag
	) {
		panel.flags['crosshair.xAxis'] = new CrosshairFlagX({
			panel,
			id: 'crosshair.xAxis'
		});
	}
}

/**
 * Conceals crosshair flags on given panel
 * @param {Panel} panel - Panel on which to hide flags
 */
export function concealCrosshairFlags(panel) {
	// Conceal yAxis flag (will only exist on first panel)
	if (panel.flags['crosshair.yAxis']) panel.flags['crosshair.yAxis'].hide();

	// Conceal xAxis flag
	if (panel.flags['crosshair.xAxis']) panel.flags['crosshair.xAxis'].hide();
}

/**
 * Removes crosshair flags from given panel
 * @param {Panel} panel - Panel to remove flags from
 */
export function removeCrosshairFlags(panel) {
	['crosshair.xAxis', 'crosshair.yAxis'].forEach(id => {
		if (panel.flags[id]) {
			panel.flags[id].remove();
			delete panel.flags[id];
		}
	});
}

/**
 * Renders crosshair flags on given panel
 * @param {Panel} panel - Panel on which to render flags
 * @param {object} params - Function params
 * @param {Boolean} params.xAxis - Should the xAxis flag be rendered
 * @param {Boolean} params.xDate - The date to be shown on the xAxis flag
 * @param {Boolean} params.xValue - The coordinate where the flag should be shown
 * @param {Boolean} params.yAxis - Should the yAxis flag be rendered
 * @param {Boolean} params.yValue - The coordinate where the flag should be shown
 */
export function renderCrosshairFlags(panel, { xAxis = false, xDate, xValue, yAxis = false, yValue }) {
	// Render xAxis flag
	if (xAxis && panel.flags['crosshair.xAxis']) {
		// Update flag & label
		panel.flags['crosshair.xAxis'].render(xValue, xDate);
		panel.flags['crosshair.xAxis'].hide();
	}

	// Render yAxis flag
	if (yAxis && panel.flags['crosshair.yAxis']) {
		// Update flag & label
		panel.flags['crosshair.yAxis'].render(panel.yAxis.primaryScale.invert(yValue));
		panel.flags['crosshair.yAxis'].hide();
	}
}

/**
 * Reveals crosshair flags on given panel
 * @param {Panel} panel - Panel on which to reveal flags
 */
export function revealCrosshairFlags(panel) {
	// Show yAxis flag (will only exist on first panel)
	if (panel.flags['crosshair.yAxis']) panel.flags['crosshair.yAxis'].show();

	// Show xAxis flag
	if (panel.flags['crosshair.xAxis']) panel.flags['crosshair.xAxis'].show();
}

export default {
	addCrosshairFlags,
	buildCrosshairData,
	concealCrosshairFlags,
	removeCrosshairFlags,
	renderCrosshairFlags,
	revealCrosshairFlags
};
